<template>
  <div
    ref="preCacheRef"
    class="block relative z-10 overflow-hidden rounded-md shadow-md group"
    @click="handleClick($event)">
    <template v-if="controls && !isReordering">
      <div class="flex absolute inset-0 z-20 flex-wrap pointer-events-none content-start">
        <button
          class="flex justify-center items-center w-1/4 aspect-square md:w-8 md:h-8 text-white cursor-pointer pointer-events-auto focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-600"
          :class="[stateButtonClass]"
          @click.prevent.stop="toggleSeen">
          <fa-icon
            v-if="isBusy"
            :icon="['fas', 'spinner-third']"
            class="text-sm md:text-base fa-fw fa-spin" />
          <fa-icon
            v-else
            :icon="stateIcon"
            class="text-sm md:text-base fa-fw" />
        </button>

        <span
          v-if="isReviewed"
          class="flex justify-center items-center w-1/4 aspect-square md:w-8 md:h-8 text-white bg-yellow-500 opacity-90 pointer-events-auto">
          <fa-icon :icon="['fas', 'hundred-points']" class="text-sm md:text-base fa-fw" />
        </span>
        <span
          v-if="isInList"
          class="flex justify-center items-center w-1/4 aspect-square md:w-8 md:h-8 text-white bg-blue-500 opacity-90 pointer-events-auto">
          <fa-icon :icon="['fas', 'list-ol']" class="text-sm md:text-base fa-fw" />
        </span>
        <span
          v-if="isFaved"
          class="flex justify-center items-center w-1/4 aspect-square md:w-8 md:h-8 text-white bg-red-500 opacity-90 pointer-events-auto">
          <fa-icon :icon="['fas', 'heart']" class="text-sm md:text-base fa-fw" />
        </span>
      </div>
    </template>

    <template v-if="isReordering">
      <span
        class="absolute top-0 z-10 p-1 w-full rounded-t text-black/40 bg-white/80 hover:opacity-80 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-600">
        <fa-icon
          :icon="['far', 'arrows-up-down-left-right']"
          size="lg"
          class="fa-fw" />
        <span class="text-xs font-bold"></span>
      </span>
    </template>
    <TitlePosterImage
      :path="title?.poster_path"
      :lightbox="lightbox"
      :lightbox-images="posterLightboxUrls"
      :crop="crop" />
  </div>
</template>
<script setup lang="ts">
import { computed, watch, ref, inject, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreReviews } from '@/stores/storeReviews'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreAuth } from '@/stores/storeAuth'
import { useTitleActions } from '@/use/useTitleActions'
import { usePreCache } from '@/composables/usePreCache'
import TitlePosterImage from '@/components/Title/TitlePosterImage'

const router = useRouter()
const storeTitlesUser = useStoreTitlesUser()
const storeReviews = useStoreReviews()
const storeLists = useStoreLists()
const storeAuth = useStoreAuth()

// Inject list context if available (for card navigation)
const listContext = inject('listContext', null)

const props = defineProps({
  title: Object,
  titleType: String,
  lightbox: {
    type: Boolean,
    default: false
  },
  controls: {
    type: Boolean,
    default: true
  },
  isReordering: {
    type: Boolean,
    default: false
  },
  useContextNav: {
    type: Boolean,
    default: false
  },
  crop: {
    type: Object,
    default: null
  }
})

// Build full poster URLs for lightbox (main poster + alternatives)
const posterLightboxUrls = computed(() => {
  const baseUrl = 'https://image.tmdb.org/t/p/original'
  const urls = []
  // Main poster first
  if (props.title?.poster_path) {
    urls.push(baseUrl + props.title.poster_path)
  }
  // Alternative posters from images.posters
  if (props.title?.images?.posters?.length) {
    for (const poster of props.title.images.posters) {
      const url = baseUrl + poster.file_path
      if (!urls.includes(url)) {
        urls.push(url)
      }
    }
  }
  return urls
})

// Set up pre-cache registration for visible titles
const { elementRef: preCacheRef } = usePreCache({
  id: computed(() => props.title?.id),
  type: 'title',
  tmdbId: computed(() => props.title?.id),
  tmdbType: computed(() => props.titleType || props.title?.media_type)
})

// Keep a simple busy state for the spinner in template
const isBusy = ref(false)

const tmdbId = computed(() => {
  if (props.title) {
    return props.title.id
  }
  return null
})

const fetchTitleSoft = () => {
  if (tmdbId.value) {
    const titleQuery = {
      id: tmdbId.value,
      type: props.titleType
    }
    // Use batched fetching for better performance
    storeTitlesUser.queueTitleForBatch(titleQuery)
  }
}

watch(tmdbId, fetchTitleSoft, { immediate: true })

const userTitleInfo = computed(() => {
  if (tmdbId.value) {
    const titleQuery = {
      id: tmdbId.value,
      type: props.titleType
    }
    return storeTitlesUser.getTitleByTitleId(titleQuery)
  }

  return null
})

// Create the data structure that useTitleActions expects
const actionsData = computed(() => {
  // Need to add media_type to the title object since useTitleActions expects it there
  const titleWithMediaType = props.title
    ? {
        ...props.title,
        media_type: props.titleType
      }
    : null

  return {
    title: titleWithMediaType,
    titleInfo: null, // TitlePosterCard doesn't have titleInfo, only TMDB data
    userTitleInfo: userTitleInfo.value,
    type: props.titleType,
    tvSeason: null,
    tvEpisode: null
  }
})

// Use the same actions helper as TitleActionsBar
const {
  toggleSeen: originalToggleSeen,
  isSeen,
  isFaved,
  isWatchlisted,
  isInProgress,
  usesLogSystem,
  derivedState
} = useTitleActions(actionsData)

// Check if user has reviewed this title (exclude drafts)
const isReviewed = computed(() => {
  const userId = storeAuth.user?.id
  const titleId = tmdbId.value
  if (!userId || !titleId) return false
  // Filter directly from reviews array for more reliable reactivity
  const userReviews = storeReviews.reviews.filter(
    (review) =>
      review &&
      review.user_id === userId &&
      review.status !== 'draft' &&
      (review.tmdb_id == titleId || review.titles?.tmdb_id == titleId)
  )
  return userReviews.length > 0
})

// Check if title is in any of user's non-system/non-auto lists
const isInList = computed(() => {
  if (!tmdbId.value) return false
  const userId = storeAuth.user?.id
  if (!userId) return false

  const listItems = storeLists.items.filter((item) => {
    // Match the title
    if (
      item.titles?.tmdb_id != tmdbId.value ||
      item.titles?.tmdb_type != props.titleType
    ) {
      return false
    }
    // Exclude auto and system lists (watchlist, seen, favourites, circle)
    // These have their own dedicated UI indicators
    const list = storeLists.lists.find((l) => l.id === item.list_id)
    if (!list || list.user_id !== userId || list.is_auto || list.is_system) return false
    return true
  })
  return listItems.length > 0
})

// Wrap toggleSeen to manage local busy state for UI spinner
const toggleSeen = async () => {
  isBusy.value = true
  try {
    await originalToggleSeen()
  } finally {
    isBusy.value = false
  }
}

// Log system state indicator computeds
const stateIcon = computed(() => {
  if (!usesLogSystem.value) {
    // Legacy system - just show seen state
    return isSeen.value ? ['fas', 'hexagon-check'] : ['far', 'hexagon-check']
  }

  // Log system - show based on derived state
  switch (derivedState.value) {
    case 'seen':
      return ['fas', 'hexagon-check']
    case 'in_progress':
      return ['fas', 'play-circle']
    case 'watchlist':
      return ['fas', 'bookmark']
    case 'dropped':
      return ['fas', 'circle-stop']
    default:
      return ['far', 'hexagon-check']
  }
})

const stateButtonClass = computed(() => {
  if (!usesLogSystem.value) {
    // Legacy system
    return isSeen.value
      ? 'bg-green-500 hover:text-white/80 opacity-80 hover:opacity-90'
      : 'bg-transparent hover:bg-green-500 opacity-70 hover:opacity-80'
  }

  // Log system - color based on derived state
  switch (derivedState.value) {
    case 'seen':
      return 'bg-green-500 hover:text-white/80 opacity-80 hover:opacity-90'
    case 'in_progress':
      return 'bg-blue-500 hover:text-white/80 opacity-80 hover:opacity-90'
    case 'watchlist':
      return 'bg-amber-500 hover:text-white/80 opacity-80 hover:opacity-90'
    case 'dropped':
      return 'bg-red-500 hover:text-white/80 opacity-80 hover:opacity-90'
    default:
      return 'bg-transparent hover:bg-green-500 opacity-70 hover:opacity-80'
  }
})

const hasAnyLogState = computed(() => {
  if (!usesLogSystem.value) return isSeen.value
  return derivedState.value !== null
})

const showControls = computed(() => {
  return true
})

// Route config for this title
const titleRoute = computed(() => {
  if (!props.title?.id) return null
  return {
    name: 'WebTitle',
    params: {
      id: props.title.id,
      type: props.titleType || props.title.media_type
    }
  }
})

// Handle click with optional list context
const handleClick = (event) => {
  if (!props.title?.id) return

  const route = titleRoute.value
  if (!route) return

  // If using context nav and in a list context, use context-aware navigation
  if (props.useContextNav && listContext) {
    // Stop propagation so parent wrapper handlers don't also navigate
    event?.stopPropagation()
    listContext.navigateWithContext(props.title.id, 'title', route, {
      tmdbId: props.title.id,
      tmdbType: props.titleType || props.title.media_type
    })
  } else if (!props.useContextNav) {
    // Normal navigation only if not using context nav
    // (if useContextNav is true but no context, do nothing - let parent handle it)
    router.push(route)
  }
}

// Register with list context on mount
onMounted(() => {
  if (listContext && props.title?.id && props.useContextNav) {
    listContext.registerItem({
      id: props.title.id,
      type: 'title',
      tmdbId: props.title.id,
      tmdbType: props.titleType || props.title.media_type
    })
  }
})
</script>
